import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CTAButton, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { SOURCES, STATUSES } from './sources';

const todayPlus = (days = 1) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Module 6 — Action log editor (create + edit).
 *
 * Mobile  : bottom sheet · Desktop : centered modal
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSubmit: (payload) => Promise — caller does the supabase upsert
 *  - onDelete: (id) => Promise — only when editing
 *  - initial: existing action_log row OR prefill {source_activity, title,
 *             description, source_entity_id, source_entity_type}
 */
const ActionLogEditor = ({ open, onClose, onSubmit, onDelete, initial }) => {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(todayPlus(1));
  const [sourceActivity, setSourceActivity] = useState('strategic_focus');
  const [status, setStatus] = useState('open');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || '');
    setDescription(initial?.description || '');
    setDueDate(initial?.due_date || todayPlus(1));
    setSourceActivity(initial?.source_activity || 'strategic_focus');
    setStatus(initial?.status || 'open');
    setOutcomeNotes(initial?.outcome_notes || '');
    setError(null);
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError(t('tcn.m6.editor.required', 'Title and description are required'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit?.({
        ...(isEdit ? { id: initial.id } : {}),
        title: title.trim(),
        description: description.trim(),
        due_date: dueDate,
        source_activity: sourceActivity,
        source_entity_type: initial?.source_entity_type || 'none',
        source_entity_id: initial?.source_entity_id ?? null,
        status,
        outcome_notes: outcomeNotes.trim() || null,
      });
      onClose?.();
    } catch (err) {
      setError(err?.message || t('tcn.m6.editor.error', 'Failed to save'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (typeof window !== 'undefined' && !window.confirm(t('tcn.m6.editor.deleteConfirm', 'Delete this action plan?'))) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete?.(initial.id);
      onClose?.();
    } catch (err) {
      setError(err?.message || t('tcn.m6.editor.error', 'Failed to delete'));
    } finally {
      setDeleting(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        animation: 'tcnFade 160ms ease-out',
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: '92vh',
          overflowY: 'auto',
          background: TCN.surface1,
          border: `1px solid ${TCN.borderStrong}`,
          borderRadius: '20px 20px 0 0',
          padding: 20,
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
          color: TCN.text,
          fontFamily: TCN.fontSans,
          animation: 'tcnSlideUp 220ms cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            {isEdit
              ? t('tcn.m6.editor.editTitle', 'Edit action plan')
              : t('tcn.m6.editor.createTitle', 'New action plan')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: TCN.textDim,
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field
            label={t('tcn.m6.editor.source', 'Source')}
            type="select"
            value={sourceActivity}
            onChange={setSourceActivity}
            options={SOURCES.map(s => ({
              value: s.id,
              label: t(`tcn.m6.source.${s.id}`, s.label),
            }))}
          />

          <Field
            label={t('tcn.m6.editor.titleField', 'Title')}
            value={title}
            onChange={setTitle}
            placeholder={t('tcn.m6.editor.titlePh', 'Push lyon.w.7 to T6')}
          />
          <Field
            label={t('tcn.m6.editor.description', 'Description')}
            value={description}
            onChange={setDescription}
            multiline
            placeholder={t('tcn.m6.editor.descPh', 'Coach 2h LIVE/day this week, share tier criteria')}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field
              label={t('tcn.m6.editor.dueDate', 'Due date')}
              type="date"
              value={dueDate}
              onChange={setDueDate}
            />
            <Field
              label={t('tcn.m6.editor.statusField', 'Status')}
              type="select"
              value={status}
              onChange={setStatus}
              options={STATUSES.map(s => ({
                value: s.id,
                label: t(`tcn.m6.status.${s.id}`, s.label),
              }))}
            />
          </div>

          {(status === 'done' || status === 'cancelled' || isEdit) && (
            <Field
              label={t('tcn.m6.editor.outcomeNotes', 'Outcome notes')}
              value={outcomeNotes}
              onChange={setOutcomeNotes}
              multiline
              placeholder={t('tcn.m6.editor.outcomePh', 'How did it go?')}
            />
          )}
        </div>

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              borderRadius: 8,
              background: `${TCN.red}1f`,
              border: `1px solid ${TCN.red}33`,
              color: TCN.red,
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            marginTop: 18,
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          {isEdit && (
            <CTAButton
              variant="secondary"
              type="button"
              onClick={handleDelete}
              loading={deleting}
              fullWidth={false}
              style={{ color: TCN.red, borderColor: `${TCN.red}55` }}
            >
              {t('tcn.m6.editor.delete', 'Delete')}
            </CTAButton>
          )}
          <div style={{ display: 'flex', gap: 10, marginInlineStart: 'auto', flex: 1, maxWidth: '100%' }}>
            <CTAButton variant="secondary" type="button" onClick={onClose}>
              {t('tcn.m6.editor.cancel', 'Cancel')}
            </CTAButton>
            <CTAButton variant="primary" type="submit" loading={submitting}>
              {isEdit
                ? t('tcn.m6.editor.save', 'Save')
                : t('tcn.m6.editor.create', 'Create')}
            </CTAButton>
          </div>
        </div>
        <style>{`
          @keyframes tcnFade { from { opacity: 0 } to { opacity: 1 } }
          @keyframes tcnSlideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
        `}</style>
      </form>
    </div>,
    document.body
  );
};

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  multiline = false,
  options,
}) => {
  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    background: TCN.surface2,
    border: `1px solid ${TCN.border}`,
    borderRadius: 10,
    color: TCN.text,
    fontFamily: 'inherit',
    // FRM-001/TCH-001 — 16px : pas de zoom iOS au focus (app manager mobile).
    fontSize: 16,
    outline: 'none',
    boxSizing: 'border-box',
  };
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          color: TCN.textDim,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {type === 'select' ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}
    </div>
  );
};

export default ActionLogEditor;
